import Link from "next/link";
import {
  GetStaticPaths,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from "next";
import dynamic from "next/dynamic";
import { VFC } from "react";

import { NextSeo } from "next-seo";
import Layout from "components/layout";
import ArticleLinks from "components/articles/articleLinks";
import Toc from "components/articles/toc";
import { MiniLoader } from "components/utils";

import { admin_db } from "lib/firebase-admin";
import { signin } from "lib/signin";
import { convertDate, getOgiUrl } from "lib/utils";
import { deltaToHtml } from "lib/deltaToHtml";
import { useTheme } from "next-themes";
import { useUser } from "hooks/useUser";
import { useLike } from "hooks/articles/useLike";
import { useChangeThemeStyles } from "hooks/useChangeThemeStyles";
import { useClientFetchArticle } from "hooks/articles/useClientFetchArticle";

import { Article, CurrentUser } from "lib/type";

import styles from "styles/article.module.scss";

const LikeShare = dynamic(() => import("components/articles/likeShare"), {
  loading: () => <MiniLoader />,
});
const Comments = dynamic(() => import("components/articles/comments"), {
  loading: () => <MiniLoader />,
});

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

let article: Article<string> = {
  author: null,
  articleID: "",
  thumb: "",
  color: "#ffffff",
  title: "",
  tags: [],
  body: "",
  createdAt: "",
  updateAt: "",
  likeCount: 0,
  commentCount: 0,
};

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const articleID = context.params?.articleID;
  if (!articleID || typeof articleID !== "string")
    return { props: { article } };
  const snapShot = await admin_db
    .collectionGroup("publicArticles")
    .where("articleID", "==", articleID)
    .get()
    .catch((err) => {
      console.log(err);
      return null;
    });
  if (!snapShot || snapShot.docs.length !== 1) return { props: { article } };
  const articles = snapShot.docs.map((doc) => doc.data());
  const {
    authorUid,
    thumb,
    color,
    title,
    tags,
    body,
    createdAt,
    updateAt,
    likeCount,
    commentCount,
  } = articles[0];
  const authorRef = admin_db.collection("users").doc(authorUid);
  const authorDoc = await authorRef.get().catch((err) => {
    console.log(err);
    return null;
  });
  if (!authorDoc || !authorDoc.exists) return { props: { article } };
  const author = (await JSON.parse(
    JSON.stringify(authorDoc.data())
  )) as CurrentUser;
  const html = deltaToHtml(body);
  const crateDate = convertDate(createdAt);
  const updateDate = updateAt ? convertDate(updateAt) : "";
  article = {
    author: author,
    articleID: articleID,
    thumb: thumb,
    color: color,
    title: title,
    tags: tags,
    body: html,
    createdAt: crateDate,
    updateAt: updateDate,
    likeCount: likeCount,
    commentCount: commentCount,
  };

  return { props: { article }, revalidate: 60 };
};

type ArticleProps = InferGetStaticPropsType<typeof getStaticProps>;

const ArticlePage: VFC<ArticleProps> = (props) => {
  const { user, isAuthChecking } = useUser();
  const { article } = useClientFetchArticle(props, user);
  const {
    author,
    articleID,
    thumb,
    color,
    title,
    tags,
    body,
    createdAt,
    updateAt,
  } = article;

  const url = `${process.env.NEXT_PUBLIC_WEB_URL}/articles/${articleID}`;
  const description = body.replace(/(<([^>]+)>)/gi, "").substr(0, 200) + "...";
  const authorName = author?.name as string;
  const authorColor = author?.color as string;

  const meta = {
    title: title,
    description: description,
    canonical: url,
    openGraph: {
      url: url,
      title: title,
      description: description,
      images: [
        {
          url: title ? getOgiUrl(title, thumb, authorName, authorColor) : "",
          alt: title,
        },
      ],
      site_name: process.env.NEXT_PUBLIC_SITE_NAME,
    },
    twitter: {
      site: url,
      cardType: "summary_large_image",
    },
  };

  const { isCurrentLiked, currentLikeCount, changeLike } = useLike(
    author,
    user,
    articleID,
    title,
    color,
    thumb,
    createdAt
  );

  const { theme } = useTheme();
  const { themeStyle } = useChangeThemeStyles(
    theme,
    styles.article,
    styles.articleDark
  );

  return (
    <Layout
      lists={[
        {
          string: title,
        },
      ]}
    >
      <div className="lg:grid lg:grid-cols-12">
        {articleID ? (
          <>
            <NextSeo {...meta} />
            <div className="lg:border-light-700 container relative mb-4 overflow-hidden pb-4 lg:col-span-9 lg:border-b lg:border-solid lg:px-0">
              <h1 className="mb-2 justify-start">{title}</h1>
              <p className="absolute top-2 right-0 rotate-12 transform text-9xl opacity-20">
                {thumb}
              </p>
              <ArticleLinks
                {...{
                  isAuthChecking,
                  author,
                  user,
                  articleID,
                  tags,
                  thumb,
                  createdAt,
                  updateAt,
                }}
              />
            </div>
            <nav
              className="shadow-light-700 dark:shadow-dark-400 bg-light dark:bg-dark sticky top-0 z-20
              ml-auto flex max-h-screen flex-col rounded border-t-[0.5rem] border-solid bg-opacity-95 px-4 pt-2 shadow-lg backdrop-blur-sm dark:bg-opacity-95
              lg:fixed lg:left-[calc(50%+260px)] lg:top-[5.5rem] lg:max-h-[calc(100vh-80px)] lg:w-[224px] lg:!bg-opacity-0 lg:py-4 lg:shadow-none
              xl:left-[calc(50%+320px)] xl:w-[300px]"
              style={{ borderColor: color }}
            >
              <Toc
                {...{
                  isAuthChecking,
                  user,
                  title,
                  url,
                  isCurrentLiked,
                  currentLikeCount,
                  changeLike,
                  signin,
                }}
              />
            </nav>
            {body && (
              <article
                className={`${themeStyle} container mb-12 p-4 leading-8 lg:col-span-9 lg:px-0`}
                dangerouslySetInnerHTML={{
                  __html: body,
                }}
              />
            )}
            <div className="container mb-4 lg:col-span-9 lg:px-0">
              <LikeShare
                {...{
                  isAuthChecking,
                  user,
                  title,
                  url,
                  isCurrentLiked,
                  currentLikeCount,
                  changeLike,
                }}
              />
            </div>
            <div className="container mb-8 lg:col-span-9 lg:px-0">
              <ArticleLinks
                {...{
                  isAuthChecking,
                  author,
                  user,
                  articleID,
                  tags,
                  thumb,
                  createdAt,
                  updateAt,
                }}
              />
            </div>
            <div className="md:container lg:col-span-9 lg:px-0">
              <Comments
                {...{ user, author, articleID, signin }}
                articleTitle={title}
              />
            </div>
          </>
        ) : (
          <section className="lg:col-span-9">
            <h1 className="mb-8">現在この記事は読めません。</h1>
            <div className="container md:px-0">
              <p>下記の原因の可能性があります。</p>
              <ul className="mb-4 list-disc pl-4">
                <li>公開されたばかり</li>
                <li>削除された</li>
                <li>非公開になった</li>
                <li>元々存在しなかった</li>
              </ul>
              <Link href="/" prefetch={false}>
                <a className="underline">読める記事を探す</a>
              </Link>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
};

export default ArticlePage;
